// src/utils/crypto.js

export async function encryptText(text, password) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    // Hash the password to get a 256-bit key
    const passwordBuffer = encoder.encode(password);
    const keyHash = await window.crypto.subtle.digest('SHA-256', passwordBuffer);

    // Import the key
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyHash,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    // Generate a random IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // Encrypt
    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    // Combine IV and encrypted data
    const result = new Uint8Array(iv.length + encrypted.byteLength);
    result.set(iv, 0);
    result.set(new Uint8Array(encrypted), iv.length);

    // Convert to Base64 (Unicode-safe)
    const binString = Array.from(result, (byte) => String.fromCharCode(byte)).join("");
    return 'enc:' + btoa(binString);
  } catch (error) {
    console.error("Encryption failed:", error);
    throw new Error("Encryption failed");
  }
}

export async function decryptText(encryptedBase64, password) {
  try {
    if (!encryptedBase64.startsWith('enc:')) return encryptedBase64;
    const base64Content = encryptedBase64.substring(4);
    
    const binString = atob(base64Content);
    const rawData = new Uint8Array(binString.length);
    for (let i = 0; i < binString.length; i++) {
      rawData[i] = binString.charCodeAt(i);
    }

    const iv = rawData.slice(0, 12);
    const encrypted = rawData.slice(12);

    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);
    const keyHash = await window.crypto.subtle.digest('SHA-256', passwordBuffer);

    const key = await window.crypto.subtle.importKey(
      'raw',
      keyHash,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );

    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error("Decryption failed:", error);
    throw new Error("Decryption failed. Invalid passcode.");
  }
}
